import { ProductItemInterface } from "@/assets/interfaces/products";
import { isTieredDiscountApplied } from "@/assets/js/utils/utils";
import Badge from "@/components/ui/badge";
import classNames from "classnames";
import React from "react";

interface DiscountBadgeProps {
  percentage: number;
  className?: string;
  mode?: "dark" | "light";
}

interface OptionsBadgeProps {
  options: number;
  className?: string;
}

const DiscountBadge: React.FC<DiscountBadgeProps> = ({ percentage, className, mode = "light" }) => {
  return (
    <div
      className={classNames(
        "rounded-[30px] px-2 py-0.5 text-xxxs  font-semibold flex whitespace-nowrap mb-0.5",
        className,
        {
          "bg-discount-badge text-white": mode === "dark",
          "bg-discount-badge bg-opacity-[0.1] text-discount-badge ": mode === "light",
        }
      )}
    >
      {percentage}% OFF
    </div>
  );
};

const DiscountBadgeImage: React.FC<DiscountBadgeProps> = ({ percentage, className, mode = "light" }) => {
  return (
    <div
      className={classNames(
        "rounded-[5px] px-2 py-1 text-xxs font-semibold flex whitespace-nowrap mb-0.5 items-center",
        className,
        {
          "bg-discount-badge text-white": mode === "dark",
          "bg-discount-badge bg-opacity-[0.1] text-discount-badge ": mode === "light",
        }
      )}
    >
      {percentage}% OFF
    </div>
  );
};

const OptionsBadge: React.FC<OptionsBadgeProps> = ({ options, className }) => {
  return (
    <div
      className={classNames(
        "items-center gap-x-1 rounded-[5px] px-1.75 py-1 sm:px-2 sm:py-1.25 text-xs bg-grey-fields-100 font-semibold inline-flex text-primary-500 flex-nowrap whitespace-nowrap",
        className
      )}
    >
      <span>
        {/* {options} {options > 1 ? "Options" : "Option"} */}
        Has Options
      </span>
    </div>
  );
};

interface BulkDiscountBadgeProps {
  item: ProductItemInterface;
  quantity: number;
}

const BulkDiscountBadge = ({ item, quantity }: BulkDiscountBadgeProps) => {
  const discountIsApplied = isTieredDiscountApplied(item, quantity);

  if (!discountIsApplied) return null;

  return (
    <div className="flex items-center text-primary-500 text-xxs font-semibold uppercase py-0.5">
      {/* prettier-ignore */}
      <svg width="16" viewBox="0 0 24 24" fill="none" className="mr-1">
        <path d="M21.5289 10.8689L20.0089 9.34891C19.7489 9.08891 19.5389 8.57891 19.5389 8.21891V6.05891C19.5389 5.17891 18.8189 4.45891 17.9389 4.45891H15.7889C15.4289 4.45891 14.9189 4.24891 14.6589 3.98891L13.1389 2.46891C12.5189 1.84891 11.4989 1.84891 10.8789 2.46891L9.33891 3.98891C9.08891 4.24891 8.57891 4.45891 8.20891 4.45891H6.05891C5.17891 4.45891 4.45891 5.17891 4.45891 6.05891V8.20891C4.45891 8.56891 4.24891 9.07891 3.98891 9.33891L2.46891 10.8589C1.84891 11.4789 1.84891 12.4989 2.46891 13.1189L3.98891 14.6389C4.24891 14.8989 4.45891 15.4089 4.45891 15.7689V17.9189C4.45891 18.7989 5.17891 19.5189 6.05891 19.5189H8.20891C8.56891 19.5189 9.07891 19.7289 9.33891 19.9889L10.8589 21.5089C11.4789 22.1289 12.4989 22.1289 13.1189 21.5089L14.6389 19.9889C14.8989 19.7289 15.4089 19.5189 15.7689 19.5189H17.9189C18.7989 19.5189 19.5189 18.7989 19.5189 17.9189V15.7689C19.5189 15.4089 19.7289 14.8989 19.9889 14.6389L21.5089 13.1189C22.1589 12.5089 22.1589 11.4889 21.5289 10.8689ZM7.99891 8.99891C7.99891 8.44891 8.44891 7.99891 8.99891 7.99891C9.54891 7.99891 9.99891 8.44891 9.99891 8.99891C9.99891 9.54891 9.55891 9.99891 8.99891 9.99891C8.44891 9.99891 7.99891 9.54891 7.99891 8.99891ZM9.52891 15.5289C9.37891 15.6789 9.18891 15.7489 8.99891 15.7489C8.80891 15.7489 8.61891 15.6789 8.46891 15.5289C8.17891 15.2389 8.17891 14.7589 8.46891 14.4689L14.4689 8.46891C14.7589 8.17891 15.2389 8.17891 15.5289 8.46891C15.8189 8.75891 15.8189 9.23891 15.5289 9.52891L9.52891 15.5289ZM14.9989 15.9989C14.4389 15.9989 13.9889 15.5489 13.9889 14.9989C13.9889 14.4489 14.4389 13.9989 14.9889 13.9989C15.5389 13.9989 15.9889 14.4489 15.9889 14.9989C15.9889 15.5489 15.5489 15.9989 14.9989 15.9989Z" fill="currentColor"/>
      </svg>
      Bulk Discount
    </div>
  );
};

export { DiscountBadge, OptionsBadge, DiscountBadgeImage, BulkDiscountBadge };
